import Vue from 'vue'
import store from '@/store'
import { getId } from '@/composables/currentUser'
import { mixins } from '@/mixins'
import router from '@/router'
import { addDataToLocalStorageByPrefix } from '@/composables/localStorageHelper'
import appConstants from '@/constants/constants'
import { entityLead, entityPatient } from '@/libs/acl/entities'
import ability from '@/libs/acl/ability'
import { routeIsAvailable } from '@/composables/permissionsHelper'
import { saveFileFromResponse } from '@/composables/fileHelper'
import { msgBoxConfirmAction } from '@/composables/bvModal'
import {
  showCenteredToast, showFailedOperationEternalToast, showNewLeadToast, showPatientProcessedAppointmentToast,
  showSuccessOperationEternalToast, showNewActToast, showPatientCameInToAppointmentToast,
} from '@/composables/toast'
import bus from '@/bus'

export const usersLimitNotify = (
  text = 'Вы превысили максимально доступное количество пользователей для вашего тарифа.'
) => showCenteredNotify('users-limit-notify', 'Внимание!', text)

export const subscriptionExpiredNotify = async text => {
  await loadOnlineSystemNotifications()
  showCenteredNotify('subscription-expired-notify', 'Уведомление!', text)
}

export const minimumSmsNotificationBalanceNotify = async text => {
  await loadOnlineSystemNotifications()
  showCenteredNotify('minimum-sms-notification-balance', 'Уведомление!', text)
}

export const systemNotify = notif => {
  showCenteredNotify(
    'system-notify',
    'Уведомление!',
    notif.text,
    notif.id ? notif.id : null,
    notif.files && notif.files.length ? notif.files : []
  )
}

export const newLeadNotify = notif => {
  showNewLeadToast(
    notif.message.id,
    notif.message.date,
    notif.message.source,
    notif.message.patient_full_name,
    notif.message.mobile_phone_code,
    notif.message.mobile_phone,
    notif.message.time,
    notif.message.chat_id,
    {},
    {closeOnClick: true, timeout: false, position: 'bottom-left'}
  )
}

export const newActNotify = notif => {
  const message = notif.message
  const notifId = `${message.act_id}_${message.payment_id}`
  showNewActToast(
    notif.message.text,
    notif.message.act_number,
    notif.message.payment_id,
    notif.message.payment_date,
    {'click-toast': () => processToastClick(notif.msg_action_type, notifId)},
    {
      closeOnClick: true, timeout: false, position: 'top-center', toastClassName: 'centered', draggable: false,
      id: `toast-centered-${notifId}`
    }
  )
}

export const processIncomingPatientCallNotif = data => {
  const message = data.message
  if (message.patient_id) {
    processIncomingPatientCallNotifAsPatient(message)
  } else if (message.lead_history_id) {
    processIncomingPatientCallNotifAsLead(message, false)
  } else {
    processIncomingPatientCallNotifAsLead(message, true)
  }
}

const processIncomingPatientCallNotifAsLead = async (message, newLead = false) => {
  const routeName = 'apps-leads'
  if (!ability.can('read', entityLead) || !routeIsAvailable(routeName)) {
    return
  }
  const title = `Входящий звонок с номера ${message.phone_code}${message.phone_number}. Перейти к лиду?`
  await msgBoxConfirmAction(title)
    .then(result => {
      if (result) {
        if (newLead) {
          addDataToLocalStorageByPrefix(
            appConstants.lead.onlineNotif.APP_NEW_LEADS_LOCAL_STORAGE_KEY,
            {isNewLead: true, code: message.phone_code, number: message.phone_number}
          )
        } else {
          addDataToLocalStorageByPrefix(
            appConstants.lead.onlineNotif.APP_NEW_LEADS_LOCAL_STORAGE_KEY,
            {id: message.lead_history_id, date: message.date}
          )
        }
        if (router.currentRoute.name !== 'apps-leads') {
          router.push({name: 'apps-leads'})
        } else {
          window.location.reload()
        }
      }
    })
}

const processIncomingPatientCallNotifAsPatient = async message => {
  const routeName = 'patient-cards'
  if (!ability.can('read', entityPatient) || !routeIsAvailable(routeName)) {
    return
  }
  const title = `Входящий звонок с номера ${message.phone_code}${message.phone_number} (Пациент ` +
    `${message.patient_full_name}). Открыть карточку пациента?`
  await msgBoxConfirmAction(title)
    .then(result => {
      if (result) {
        router.push({
          name: 'app-user-edit',
          params: {
            patientId: message.patient_id,
            activeTabToShow: 0,
            routerBreadcrumbParams: {sourceItemLabel: message.patient_full_name}
          },
          query: {
            sourceItemLabel: message.patient_full_name,
            activeTabToShow: 0
          }
        })
      }
    })
}

export const patientProcessedAppointmentNotification = notif => {
  showPatientProcessedAppointmentToast(
    notif.message.appointment_id,
    notif.message.title,
    notif.message.patient_id,
    notif.message.patient_full_name,
    notif.message.patient_mobile_phone_code,
    notif.message.patient_mobile_phone,
    notif.message.note,
    notif.message.rate,
    mixins.methods.formatDate(notif.message.appointment_date),
    notif.message.action,
    {},
    {closeOnClick: true, timeout: false, position: 'bottom-left'}
  )
}

export const patientCameInToAppointmentNotification = async notif => {
  await loadOnlineSystemNotifications()
  showPatientCameInToAppointmentToast(
    notif.message.appointment_id,
    notif.message.title,
    notif.message.patient_id,
    notif.message.patient_full_name,
    notif.message.patient_mobile_phone_code,
    notif.message.patient_mobile_phone,
    mixins.methods.formatDateAsDayMonthYearTime(notif.message.appointment_date),
    {
      'click-toast':
        async () => await viewOnlineSystemNotification({ notif_type: notif.msg_action_type }),
      'close-toast':
        async () => await viewOnlineSystemNotification({ notif_type: notif.msg_action_type })
    },
    { closeOnClick: false, timeout: false, position: 'bottom-left' }
  )
}

const showCenteredNotify = (type, title, text, notifId = null, files = []) => {
  showCenteredToast(
    title,
    text,
    files,
    {'click-toast': () => processToastClick(type, notifId)},
    {
      closeOnClick: true, timeout: false, position: 'top-center', toastClassName: 'centered', draggable: false,
      id: `toast-centered-${notifId}`
    }
  )
}

const processToastClick = async (type, notifId) => {
  const toastClassName = 'Vue-Toastification__toast centered'
  const toastID = `toast-centered-${notifId}`
  const elems = document.getElementsByClassName(toastClassName)
  if (elems.length) {
    const elem = elems[0]
    const className = 'margin-correction'
    if (!elem.classList.contains(className)) {
      elem.classList.add(className)
    }
  }
  Vue.$toast.dismiss(toastID)
  if (type === 'subscription-expired-notify') {
    await viewOnlineSystemNotification({ notif_type: type })
  } else if (type === 'minimum-sms-notification-balance') {
    await viewOnlineSystemNotification({ notif_type: type })
  } else if (type === 'system-notify' && notifId) {
    await store.dispatch('notification/confirmSeenNotification', notifId)
  }
}

export const processUserOperationNotification = async message => {
  if (message.type === 'success') {
    if (message.data) {
      if (message.data.module) {
        switch (message.data.module) {
          case 'service-calculation':
          case 'service-export':
          case 'material-export':
          case 'report-export':
          case 'patient-export':
            if (store.getters['application/exportProcessActive']) {
              await store.dispatch(
                  'application/getExportFile', { filename: message.data.filename }
                )
                .then(({ data }) => {
                  saveFileFromResponse(data, message.data.filename)
                  showSuccessOperationEternalToast(message.text, message.title, 'InfoIcon')
                })
                .catch(() => showFailedOperationEternalToast('Не удалось сохранить файл выгрузки'))
            }
            break
          case 'service-recalculation-by-lot':
            setTimeout(() => {
              store.commit('info/SET_SERVICES_RECALCULATION_PROCESS_ACTIVE_STATUS', false)
              showSuccessOperationEternalToast(message.text, message.title, 'InfoIcon')
            }, 500)
            break
          case 'service-group-copy':
            bus.$emit('service-group-copy-finished', message.data.group_id, message.data.parent_group_id)
            showSuccessOperationEternalToast(message.text, message.title, 'InfoIcon')
            break
        }
      }
    } else {
      showSuccessOperationEternalToast(message.text, message.title, 'InfoIcon')
    }
  } else {
    showFailedOperationEternalToast(message.text, message.title)
  }
}

export const loadOnlineSystemNotifications = async () => await store.dispatch(
  'notification/getOnlineSystemNotifications'
)

export const viewOnlineSystemNotification = async bodyParams => await store.dispatch(
  'notification/confirmSeenOnlineSystemNotification', { userId: getId(), bodyParams }
).then(async () => await loadOnlineSystemNotifications())
